import React, { useState, useEffect } from 'react';
import Select from 'components/v2/select';
import { useTranslation } from 'react-i18next';
import EditableSection from 'components/v2/EditableSection';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import UserStore from 'stores/UserStore';
import {
  checkPermissonForEditShipperBillingModule,
  payloadMappingOnlyForChanges,
} from './utils';

const BillingTeamSummary = ({ fields }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <DescriptionListItem label={t('BILLING_REP')} className="">
          {fields.billing_rep}
        </DescriptionListItem>
      </div>
      <div>
        <DescriptionListItem label={t('BILLING_LEAD')} className="">
          {fields.billing_lead}
        </DescriptionListItem>
      </div>
      <div>
        <DescriptionListItem label={t('COLLECTOR')} className="">
          {fields.collector}
        </DescriptionListItem>
      </div>
    </div>
  );
};

const BillingTeamForm = ({
  fields,
  onChange,
  onSaveSuccess,
  onSaveFail,
  saving,
  onSaveShipper,
}) => {
  const { t } = useTranslation();
  const activeAdmins = UserStore.getAdminUsers() || [];

  const saveForm = async () => {
    try {
      await onSaveShipper();
      onSaveSuccess(fields);
    } catch (e) {
      onSaveFail();
    }
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Select
          data-testId="billing-rep-select"
          inputClassNames="w-40"
          items={activeAdmins}
          info={t('THE_BILLING_REP')}
          label={t('BILLING_REP')}
          valueKey="email"
          textKey="email"
          selected={fields.billing_rep}
          onSelect={(option) => {
            onChange('billing_rep', option.email);
          }}
        />
      </div>
      <div>
        <Select
          data-testId="billing-lead-select"
          inputClassNames="w-40"
          items={activeAdmins}
          label={t('BILLING_LEAD')}
          info={t('THE_BILLING_LEAD')}
          valueKey="email"
          textKey="email"
          selected={fields.billing_lead}
          onSelect={(option) => {
            onChange('billing_lead', option.email);
          }}
        />
      </div>
      <div>
        <Select
          data-testId="collector-select"
          inputClassNames="w-40"
          items={activeAdmins}
          label={t('COLLECTOR')}
          info={t('THE_COLLECTOR')}
          valueKey="email"
          textKey="email"
          selected={fields.collector}
          onSelect={(option) => {
            onChange('collector', option.email);
          }}
        />
      </div>
    </div>
  );
};

const BillingTeam = ({ data, className, onUpdateShipper }) => {
  const { t } = useTranslation();
  const [initialData, setInitialData] = useState(data);
  const [billingTeamData, setBillingTeamData] = useState(data);

  const onChange = (field, value) => {
    setBillingTeamData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAfterSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setInitialData((prev) => ({ ...prev, ...updatedData }));
      onSaveSuccess();
    };
  };

  const handleSave = async () => {
    const payload = payloadMappingOnlyForChanges(initialData, billingTeamData);
    if (Object.keys(payload).length) {
      await onUpdateShipper(payload);
    }
  };

  return (
    <EditableSection
      className={className}
      title={t('BILLING')}
      onSave={handleSave}
      canEdit={checkPermissonForEditShipperBillingModule()}
    >
      {(editMode, saving, onSaveSuccess, onSaveFail) =>
        editMode ? (
          <BillingTeamForm
            fields={billingTeamData}
            saving={saving}
            onChange={onChange}
            onSaveSuccess={handleAfterSave(onSaveSuccess)}
            onSaveFail={() => onSaveFail()}
            onSaveShipper={() => handleSave()}
          />
        ) : (
          <BillingTeamSummary fields={initialData} />
        )
      }
    </EditableSection>
  );
};

export default BillingTeam;
